import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import { CreateFilter } from '@/api/Identity/Types/CreateFilter'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { PageData } from '@/api/Pagination/Types'
import {
  AddPlatformConfiguration,
  PlatformConfigurationCountryOverridesTypesDto,
  PlatformConfigurationTypesDto,
} from '@/api/Trading-Accounts/Types/PlatformConfigurationTypesDto'
import { Pager } from '@/components/components/Pager'
import { FilterCell } from '@/components/Filter/FilterCell'
import { GridLoading } from '@/components/GridLoading'
import { Spinner } from '@/components/Spinner'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import {
  createGridState,
  GridStateStatus,
  normalizeFilter,
  useGridFilter,
  useGridState,
  useGridStatusState,
} from '@/features/DataGrid'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  selectGridFilters,
  setGridFilters,
} from '@/store/grid'
import { pageableSettings } from '@/store/grid/grid.slice'
import {
  fetchFilterAction,
  fetchFilterTypesAction,
  fetchTickmillCompaniesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from '@/store/identity/identity.actions'
import {
  fetchAllServerAction,
  fetchPlatformTypesAction,
} from '@/store/trading-accounts/trading-accounts.actions'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { AuthSessionContext } from '@/utils/AuthContext'
import { isEmpty } from '@/utils/is-empty'
import { getPaginationInitialState } from '@/utils/paginationInitialState'
import { hasPermission } from '@/utils/permissionsValidator'
import stringComparator from '@/utils/stringComparator'
import { TableWithExport } from '@/utils/table/withExport'
import { TableStateContext, TableStateContextData } from '@/utils/TableStateContext'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { Modal } from 'components/CustomModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { links } from 'utils/featureToggles'

import AddButton from '../../../../components/AddButton/AddButton'
import { useDealsBuildQuery } from '../../Deals/DealsTableFilter'
import { EditPlatformConfigurationCountryOverridesForm } from './BrockeragePlatformConfigEditCountryOverrides'
import { AddEditPlatformConfigurationForm } from './BrokeragePlatformConfigurationAddEditForm'
import brokeragePlatformConfigurationColumn from './brokeragePlatformConfigurationColumn'
import { PlatformConfigurationTableFilter } from './PlatformConfigurationTableFilter'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
  hasFilters: useAppSelector(selectGridFilters).length > 0,
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
  }
}

interface PlatformConfigurationFetchProps {
  setPlatformConfigurations: Dispatch<SetStateAction<PlatformConfigurationTypesDto[]>>
  tableData: TableStateContextData
  setTableData: Dispatch<SetStateAction<TableStateContextData>>
}

const usePlatformConfigurationFetch = ({
  setPlatformConfigurations,
  tableData,
  setTableData,
}: PlatformConfigurationFetchProps) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const { gridState, gridActions } = useGridState()
  const { filters } = useMapStateToProps()
  const { fetchFilters } = useMapDispatchToProps()
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const { getQuery, getSort } = useDealsBuildQuery()

  const fetchData = async () => {
    await fetchGrid(
      async (
        gridFilterState: GridFilterActionState
      ): Promise<PageData<PlatformConfigurationTypesDto[]>> => {
        const searchQuery = getQuery(gridFilterState.filter)
        const sortQuery = getSort(gridFilterState.sort)
        const response = await tradingAccountsApiClient.getTradingAccountPlatformTypesConfiguration(
          {
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          }
        )

        if (response?.items) {
          setPlatformConfigurations(response.items)
        }
        setTableData({ ...tableData, meta: response })

        if (!filters || filters.length === 0) await fetchFilters()

        return response
      }
    )
  }

  useEffect(() => {
    fetchData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<PlatformConfigurationTypesDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack: false })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  return { isLoading: gridStatusState.isLoading, gridStatusState, gridStatusActions, fetchData }
}

interface PlatformConfigurationPageableProps {
  setPlatformConfigurations: Dispatch<SetStateAction<PlatformConfigurationTypesDto[]>>
  tableData: TableStateContextData
  setTableData: Dispatch<SetStateAction<TableStateContextData>>
  gridStatusState: GridStateStatus
}

const usePlatformConfigurationPageable = ({
  setPlatformConfigurations,
  tableData,
  setTableData,
  gridStatusState,
}: PlatformConfigurationPageableProps) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const { gridState, gridActions } = useGridState()

  const { getQuery, getSort } = useDealsBuildQuery()

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(gridState.sort)

    gridStatusState.gridStatusActions.setFetching(true)
    const response = await tradingAccountsApiClient.getTradingAccountPlatformTypesConfiguration({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    gridStatusState.gridStatusActions.setFetching(false)

    const total = response.totalResults

    if (response?.items) {
      setPlatformConfigurations(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)

    gridStatusState.gridStatusActions.setFetching(true)
    const response = await tradingAccountsApiClient.getTradingAccountPlatformTypesConfiguration({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
    })
    gridStatusState.gridStatusActions.setFetching(false)

    if (response?.items) {
      setPlatformConfigurations(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageChange = async (page: number) => {
    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    gridStatusState.gridStatusActions.setFetching(true)
    const response = await tradingAccountsApiClient.getTradingAccountPlatformTypesConfiguration({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    gridStatusState.gridStatusActions.setFetching(false)

    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setPlatformConfigurations(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(gridState.sort)

    gridStatusState.gridStatusActions.setFetching(true)
    const response = await tradingAccountsApiClient.getTradingAccountPlatformTypesConfiguration({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    gridStatusState.gridStatusActions.setFetching(false)

    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setPlatformConfigurations(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  return { handleSortChange, handlePageChange, handlePageSizeChange, handleSkipCountChange }
}

export const BrokeragePlatformServersConfigurationPage = () => {
  const crumbs = [
    { title: 'Servers Configuration', link: links.brokeragePlatformsConfiguration.url },
  ]

  const [addEditPlatformConfigurationModal, setAddEditPlatformConfigurationModal] = useState<{
    data?: PlatformConfigurationTypesDto
    visible: boolean
  }>({
    visible: false,
  })

  const [editCountryOverridesModal, setEditCountryOverridesModal] = useState<{
    data?: PlatformConfigurationCountryOverridesTypesDto
    visible: boolean
  }>({
    visible: false,
  })

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const dispatch = useAppDispatch()

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [platformConfigurations, setPlatformConfigurations] = useState<
    PlatformConfigurationTypesDto[]
  >([])
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  const { isLoading, fetchData, ...gridStatusState } = usePlatformConfigurationFetch({
    setPlatformConfigurations,
    tableData,
    setTableData,
  })

  const { handleSortChange, handlePageChange, handlePageSizeChange, handleSkipCountChange } =
    usePlatformConfigurationPageable({
      setPlatformConfigurations,
      tableData,
      setTableData,
      gridStatusState,
    })

  const { gridState } = useGridState()

  const platformServers = useAppSelector((state) => state.tradingAccounts.allServers)
  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes)
  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)

  useEffect(() => {
    if (isEmpty(platformServers)) {
      dispatch(fetchAllServerAction(tradingAccountsApiClient))
    }
    if (isEmpty(platformTypes)) {
      dispatch(fetchPlatformTypesAction(tradingAccountsApiClient))
    }
    if (isEmpty(tickmillCompanies)) {
      dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilter = (data: PlatformConfigurationTypesDto[]) => {
    setPlatformConfigurations(data)
  }

  if (isLoading) {
    return <Spinner />
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPaginationState({
      ...getPaginationInitialState(),
      skip: 0,
      take: paginationState.take,
    })
  }

  const handleEdit = (data?: PlatformConfigurationTypesDto) => {
    setAddEditPlatformConfigurationModal({ data, visible: true })
  }
  const handleEditCountryOverrides = (data?: PlatformConfigurationCountryOverridesTypesDto) => {
    setEditCountryOverridesModal({ data, visible: true })
  }

  const handleAdd = () => {
    setAddEditPlatformConfigurationModal({ visible: true })
  }

  const onSubmitAdd = async (values: AddPlatformConfiguration) => {
    try {
      await tradingAccountsApiClient.addTradingAccountPlatformTypeConfiguration(values)
      setAddEditPlatformConfigurationModal({ visible: false })
      await fetchData()
    } catch (error: unknown) {
      console.error(error)
    }
  }

  const onSubmitEdit = async (values: AddPlatformConfiguration) => {
    try {
      const tickmillCompanyId = addEditPlatformConfigurationModal.data?.tickmillCompany?.id || null
      const platformTypeId = addEditPlatformConfigurationModal.data?.platformType?.id || null

      if (tickmillCompanyId && platformTypeId) {
        await tradingAccountsApiClient.updateTradingAccountPlatformTypeConfiguration(
          tickmillCompanyId,
          platformTypeId,
          values
        )
        setAddEditPlatformConfigurationModal({ visible: false })
        await fetchData()
      }
    } catch (error: unknown) {
      console.error(error)
    }
  }

  const gridData = filterBy(platformConfigurations || [], filter).sort(
    (a: PlatformConfigurationTypesDto, b: PlatformConfigurationTypesDto) =>
      stringComparator(a.tickmillCompany?.name ?? '', b.tickmillCompany?.name ?? '')
  )

  return (
    <>
      {addEditPlatformConfigurationModal.visible && (
        <AddEditPlatformConfigurationForm
          isOpen={addEditPlatformConfigurationModal.visible}
          onClose={() => setAddEditPlatformConfigurationModal({ visible: false })}
          onSubmit={addEditPlatformConfigurationModal.data ? onSubmitEdit : onSubmitAdd}
          data={addEditPlatformConfigurationModal.data}
        />
      )}
      {editCountryOverridesModal.visible && (
        <Modal
          onClose={() => setEditCountryOverridesModal({ visible: false })}
          visible={editCountryOverridesModal.visible}
          padding='2.5rem'
        >
          <EditPlatformConfigurationCountryOverridesForm data={editCountryOverridesModal.data} />
        </Modal>
      )}
      <Breadcrumbs crumbs={crumbs} />
      <PlatformConfigurationTableFilter
        tableData={tableData}
        setTableData={setTableData}
        gridStatusState={gridStatusState}
        onChange={handleFilter}
      />
      <div className='records-clients'>
        <AddButton
          text='Add New Server Configuration'
          onClick={handleAdd}
          disabled={
            !hasPermission(permissions, PermissionsEnum.CanEditTickmillCompanyPlatformTypes)
          }
        />
        <GridLoading isLoading={gridStatusState.gridStatusState.isFetching}>
          <TableStateContext.Provider value={[tableData, setTableData]}>
            <TableWithExport
              data={platformConfigurations}
              columns={brokeragePlatformConfigurationColumn}
            >
              <Grid
                filterable
                filter={filter}
                data={gridData.slice(
                  paginationState.skip,
                  paginationState.take + paginationState.skip
                )}
                sort={gridState.sort}
                total={gridState.total}
                take={gridState.take}
                skip={gridState.skip}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                pageable={pageableSettings}
                pager={() => (
                  <Pager
                    onSkipCountChange={handleSkipCountChange}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    page={gridState.pageIndex}
                    pageSize={gridState.pageSize}
                    take={gridState.take}
                    skip={gridState.skip}
                  />
                )}
                style={{ zIndex: 0 }}
              >
                {brokeragePlatformConfigurationColumn
                  .filter((c) => c.show)
                  .map((column, idx) => (
                    <Column key={idx + 2} {...column} />
                  ))}
                <Column
                  width={50}
                  title='Actions'
                  filterCell={FilterCell}
                  cell={(gridCellProps: GridCellProps) => (
                    <td>
                      <MoreButton
                        items={[
                          {
                            name: 'Edit',
                            'data-test': 'edit',
                            onClick: () => handleEdit(gridCellProps.dataItem),
                            disabled: !hasPermission(
                              permissions,
                              PermissionsEnum.CanEditTickmillCompanyPlatformTypes
                            ),
                          },
                          {
                            name: 'Edit Country Overrides',
                            'data-test': 'edit-country-overrides',
                            onClick: () => handleEditCountryOverrides(gridCellProps.dataItem),
                            disabled: !hasPermission(
                              permissions,
                              PermissionsEnum.CanEditTickmillCompanyPlatformTypes
                            ),
                          },
                        ]}
                        data-test='more-button'
                      />
                    </td>
                  )}
                />
              </Grid>
            </TableWithExport>
          </TableStateContext.Provider>
        </GridLoading>
      </div>
    </>
  )
}
